import { Position } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import { MemberPermissionFilter, PositionFilter } from '__generated__/graphql';
import classNames from 'classnames';
import { ArchivedStatus } from 'components/domain/archived/ArchivedPicker/ArchivedPicker';
import DropDownPicker from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import { DEFAULT_PICKER_PAGE_SIZE, findOrFetchPickerItem } from 'components/foundation/pickers/utils';
import { useDebounce } from 'hooks';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useEmployeeNameFormatter from 'hooks/ui/useEmployeeNameFormatter';
import { useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { useEmployeePickerData } from './hooks/useEmployeePickerData';

export type { EmployeePickerMember } from './hooks/useEmployeePickerData';

export interface IEmployeePickerProps {
  value: string | null;
  onSelect: (memberId: string | null) => void;
  // Ids are filtered out at the graphql level
  blacklistedIds?: string[];
  permissions?: MemberPermissionFilter;
  positionFilter?: PositionFilter;
  minWidth?: string; // has default
  className?: ClassName;
  placeholder?: string;
  error?: boolean;
  position?: Position;
  archivedStatus?: ArchivedStatus;
  includeClockedIn?: boolean;
  includedIds?: string[];
}

function EmployeePicker({
  value,
  onSelect,
  blacklistedIds,
  permissions,
  positionFilter,
  minWidth = '350px',
  className,
  placeholder,
  error,
  position,
  archivedStatus,
  includeClockedIn,
  includedIds,
}: IEmployeePickerProps) {
  const employeeNameFormatter = useEmployeeNameFormatter();
  const [searchValue, setSearchValue] = useState('');
  const searchDebounce = useDebounce(searchValue, 250);
  const [isOpen, setIsOpen] = useState(false);
  const scroller = useRef<HTMLElement>();
  const baseQueryKey = useReactQueryBaseKey();

  const { fetchPage, fetchSelected } = useEmployeePickerData({
    blacklistedIds,
    includedIds,
    permissions,
    positionFilter,
    archivedStatus,
    includeClockedIn,
  });

  function onClose() {
    setIsOpen(false);
    setSearchValue('');
  }

  function handleSelect(model: { id: string } | null) {
    onSelect(model ? model.id : null);
  }

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller.current,
    [
      ...baseQueryKey,
      'employee-picker',
      searchDebounce,
      blacklistedIds,
      includedIds,
      permissions,
      positionFilter,
      archivedStatus,
      includeClockedIn,
    ],
    (after, first) => fetchPage(after, first, searchDebounce),
    DEFAULT_PICKER_PAGE_SIZE,
    { enabled: isOpen }
  );

  const { data: selectedMember } = useQuery({
    queryKey: [...baseQueryKey, 'employee-picker-by-id', value],
    queryFn: () => findOrFetchPickerItem(value!, data, fetchSelected),
    initialData: null,
    enabled: !!value,
  });

  function renderValueTemplate() {
    if (selectedMember) {
      return <>{employeeNameFormatter(selectedMember.firstName ?? '', selectedMember.lastName ?? '')}</>;
    }

    return <></>;
  }

  function renderRow(member: { firstName?: string | null; lastName?: string | null }) {
    return (
      <SimplePickerRow
        value={employeeNameFormatter(member.firstName ?? '', member.lastName ?? '')}
        searchValue={searchValue}
      />
    );
  }

  const classes = classNames('employee-picker', className);

  return (
    <DropDownPicker
      value={value}
      dataError={isDataError}
      error={error}
      data={data ?? []}
      loadedAll={loadedAll}
      onSelect={handleSelect}
      onClose={onClose}
      onOpen={() => setIsOpen(true)}
      onSearch={setSearchValue}
      searchValue={searchValue}
      valueTemplate={renderValueTemplate()}
      renderRow={renderRow}
      minWidth={minWidth}
      position={position}
      placeholder={placeholder}
      className={classes}
      setScrollerRef={(scrollerRef) => (scroller.current = scrollerRef)}
      isLoading={isLoading}
    />
  );
}

export default EmployeePicker;
